import { Box, Typography } from '@mui/material'
import GLOBAL from "modules/global"
import type { LabelValueDto } from 'store/dashboard'

interface HorizontalLineChartProps {
  data: Array<LabelValueDto<number>>
  showValue?: boolean
  hideLegend?: boolean
  showValueInBar?: boolean
  minSegmentWidth?: number
}



function getTextColor(bgColor: string): string {
  // Parse HSL: hsl(H, S%, L%)
  const hslMatch = bgColor.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/)
  if (hslMatch) {
    const lightness = parseInt(hslMatch[3], 10)
    return lightness > 55 ? '#1a1a1a' : '#fff'
  }
  // Parse hex: #RRGGBB
  const hexMatch = bgColor.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  if (hexMatch) {
    const r = parseInt(hexMatch[1], 16)
    const g = parseInt(hexMatch[2], 16)
    const b = parseInt(hexMatch[3], 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.55 ? '#1a1a1a' : '#fff'
  }
  return '#fff'
}

export default function HorizontalLineChart({
  data = [],
  showValue = false,
  hideLegend = false,
  showValueInBar = false,
  minSegmentWidth
}: HorizontalLineChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 40,
          borderRadius: '4px',
          overflow: 'hidden',
          mb: 2
        }}
      >
        {data.map((item) => {
          const bgColor = item.color || GLOBAL.generateColorFromLabel(item.label)
          const percentage = total > 0 ? (item.value / total) * 100 : 0
          return (
            <Box
              key={item.label}
              sx={{
                flex: `${item.value} 1 0px`,
                backgroundColor: bgColor,
                transition: 'flex-grow 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: minSegmentWidth
              }}
            >
              {showValueInBar && (
                <Typography
                  variant='caption'
                  sx={{
                    color: getTextColor(bgColor),
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    lineHeight: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {`${item.value} (${percentage.toFixed(1)}%)` }
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
      {!hideLegend && (
        <Box sx={{ display: 'flex', width: '100%' }}>
          {data.map((item) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0
            return (
              <Box
                key={item.label}
                sx={{ width: `${pct}%`, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 10,
                    height: 10,
                    borderRadius: '2px',
                    backgroundColor: item.color || GLOBAL.generateColorFromLabel(item.label)
                  }}
                />
                <Typography variant='body2' color='gray' noWrap>
                  {item.label} {showValue && item.value}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
